<template>
  <div class="about-page">
    <!-- Hero Section -->
    <section class="page-hero">
      <div class="hero-overlay"></div>
      <div class="container">
        <div class="hero-content" data-aos="fade-up">
          <h1 class="page-title">About Bee's Atelier</h1>
          <p class="page-subtitle">Handcrafted with love. Creating memorable moments through unique, personalized gifts and workshops.</p>
        </div>
      </div>
    </section>

    <!-- Our Story Section -->
    <section class="our-story">
      <div class="container">
        <div class="row align-items-center g-5">
          <div class="col-lg-6" data-aos="fade-right">
            <div class="story-image-wrapper">
              <img src="https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=600&h=500&fit=crop" alt="Our Story" class="story-image">
              <div class="story-badge">
                <div class="badge-content">
                  <span class="badge-number">5+</span>
                  <span class="badge-text">Years of Crafting</span>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-6" data-aos="fade-left">
            <div class="section-header text-start">
              <span class="section-subtitle">Our Journey</span>
              <h2 class="section-title-modern">Handcrafted with Love</h2>
              <div class="title-underline ms-0"></div>
            </div>
            <p class="story-text">
              At Bee's Atelier, we believe that every gift tells a story. Founded with a passion for creating unique, 
              handmade treasures, we've been bringing joy to our customers through personalized gifts and creative workshops.
            </p>
            <p class="story-text">
              Our journey began with a simple idea: to create meaningful, handcrafted items that celebrate life's 
              special moments. From custom jewelry to personalized home decor, each piece is crafted with attention 
              to detail and infused with love.
            </p>
            <div class="story-features">
              <div class="feature-item">
                <i class="fas fa-heart"></i>
                <span>Handmade with Care</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-star"></i>
                <span>100% Unique Designs</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-smile"></i>
                <span>Customer Satisfaction</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Our Values Section -->
    <section class="our-values">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title-modern">Our Core Values</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="row g-4">
          <div class="col-md-4" data-aos="fade-up" data-aos-delay="0">
            <div class="value-card">
              <div class="value-icon">
                <i class="fas fa-paint-brush"></i>
              </div>
              <h4 class="value-title">Creativity</h4>
              <p class="value-description">
                We celebrate creativity in every piece we make, encouraging unique expression and personal style.
              </p>
            </div>
          </div>
          
          <div class="col-md-4" data-aos="fade-up" data-aos-delay="100">
            <div class="value-card">
              <div class="value-icon">
                <i class="fas fa-leaf"></i>
              </div>
              <h4 class="value-title">Sustainability</h4>
              <p class="value-description">
                We're committed to eco-friendly practices, using sustainable materials and minimizing waste.
              </p>
            </div>
          </div>
          
          <div class="col-md-4" data-aos="fade-up" data-aos-delay="200">
            <div class="value-card">
              <div class="value-icon">
                <i class="fas fa-users"></i>
              </div>
              <h4 class="value-title">Community</h4>
              <p class="value-description">
                Building a creative community through workshops and sharing the joy of handcrafted art.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Our Process Section -->
    <section class="our-process">
      <div class="container">
        <div class="section-header">
          <span class="section-subtitle">How We Work</span>
          <h2 class="section-title-modern">From Concept to Creation</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="process-timeline">
          <div class="process-step" data-aos="fade-up" data-aos-delay="0">
            <div class="step-number">01</div>
            <div class="step-content">
              <h4 class="step-title">Ideation</h4>
              <p class="step-description">We work with you to understand your vision and create a unique design concept.</p>
            </div>
          </div>
          
          <div class="process-step" data-aos="fade-up" data-aos-delay="100">
            <div class="step-number">02</div>
            <div class="step-content">
              <h4 class="step-title">Crafting</h4>
              <p class="step-description">Each piece is carefully handcrafted using quality materials and attention to detail.</p>
            </div>
          </div>
          
          <div class="process-step" data-aos="fade-up" data-aos-delay="200">
            <div class="step-number">03</div>
            <div class="step-content">
              <h4 class="step-title">Quality Check</h4>
              <p class="step-description">Every item undergoes rigorous quality inspection before it reaches you.</p>
            </div>
          </div>
          
          <div class="process-step" data-aos="fade-up" data-aos-delay="300">
            <div class="step-number">04</div>
            <div class="step-content">
              <h4 class="step-title">Delivery</h4>
              <p class="step-description">Your handcrafted treasure is carefully packaged and delivered with love.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Team Section -->
    <section class="our-team">
      <div class="container">
        <div class="section-header">
          <span class="section-subtitle">Meet The Artisans</span>
          <h2 class="section-title-modern">Our Creative Team</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="row g-4">
          <div class="col-md-4" data-aos="zoom-in" data-aos-delay="0">
            <div class="team-card">
              <div class="team-image-wrapper">
                <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=350&fit=crop" alt="Team Member" class="team-image">
                <div class="team-social">
                  <a href="#" class="social-link"><i class="fab fa-instagram"></i></a>
                  <a href="#" class="social-link"><i class="fab fa-facebook"></i></a>
                </div>
              </div>
              <div class="team-info">
                <h4 class="team-name">Sarah Johnson</h4>
                <p class="team-role">Founder & Lead Designer</p>
              </div>
            </div>
          </div>
          
          <div class="col-md-4" data-aos="zoom-in" data-aos-delay="100">
            <div class="team-card">
              <div class="team-image-wrapper">
                <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=350&fit=crop" alt="Team Member" class="team-image">
                <div class="team-social">
                  <a href="#" class="social-link"><i class="fab fa-instagram"></i></a>
                  <a href="#" class="social-link"><i class="fab fa-facebook"></i></a>
                </div>
              </div>
              <div class="team-info">
                <h4 class="team-name">Emily Davis</h4>
                <p class="team-role">Workshop Coordinator</p>
              </div>
            </div>
          </div>
          
          <div class="col-md-4" data-aos="zoom-in" data-aos-delay="200">
            <div class="team-card">
              <div class="team-image-wrapper">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=350&fit=crop" alt="Team Member" class="team-image">
                <div class="team-social">
                  <a href="#" class="social-link"><i class="fab fa-instagram"></i></a>
                  <a href="#" class="social-link"><i class="fab fa-facebook"></i></a>
                </div>
              </div>
              <div class="team-info">
                <h4 class="team-name">Michael Brown</h4>
                <p class="team-role">Craft Specialist</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-content" data-aos="fade-up">
          <h2 class="cta-title">Ready to Create Something Special?</h2>
          <p class="cta-text">Join us in crafting memorable moments with our unique handmade creations.</p>
          <div class="cta-buttons">
            <a href="/shop" class="btn-cta-primary">
              Shop Now
              <i class="fas fa-arrow-right"></i>
            </a>
            <a href="/workshops" class="btn-cta-secondary">
              Join Workshop
              <i class="fas fa-palette"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'AboutPage',
  mounted() {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-in-out',
        once: true,
        offset: 100
      });
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;500;600;700&display=swap');

.about-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ========== HERO SECTION ========== */
.page-hero {
  height: 300px;
  background: linear-gradient(135deg, rgba(118, 172, 175, 0.9), rgba(98, 152, 155, 0.9));
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: 80px;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
}

.hero-content {
  text-align: center;
  color: white;
  position: relative;
  z-index: 2;
}

.page-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.page-subtitle {
  font-size: clamp(1rem, 2vw, 1.3rem);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
}

/* ========== OUR STORY SECTION ========== */
.our-story {
  padding: 20px 0;
  background: white;
}

.story-image-wrapper {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 50px rgba(0,0,0,0.15);
}

.story-image {
  width: 100%;
  height: 500px;
  object-fit: cover;
}

.story-badge {
  position: absolute;
  bottom: 30px;
  right: 30px;
  background: white;
  padding: 25px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
}

.badge-content {
  text-align: center;
}

.badge-number {
  display: block;
  font-size: 2.5rem;
  font-weight: 700;
  color: rgb(118, 172, 175);
  font-family: 'Playfair Display', serif;
}

.badge-text {
  display: block;
  font-size: 0.9rem;
  color: #2d3748;
  font-weight: 600;
}

.section-header {
  margin-bottom: 30px;
}

.section-subtitle {
  color: rgb(118, 172, 175);
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  display: block;
  margin-bottom: 10px;
}

.section-title-modern {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 4vw, 2rem);
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 20px;
}

.title-underline {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, rgb(118, 172, 175), rgb(98, 152, 155));
  margin: 0 auto;
  border-radius: 2px;
}

.story-text {
  font-size: 1.05rem;
  line-height: 1.8;
  color: #4a5568;
  margin-bottom: 20px;
}

.story-features {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 30px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  color: #2d3748;
}

.feature-item i {
  color: rgb(118, 172, 175);
  font-size: 1.2rem;
}

/* ========== OUR VALUES SECTION ========== */
.our-values {
  padding: 20px 0;
  background: #f8fafb;
}

.value-card {
  background: white;
  padding: 40px 30px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.4s;
  height: 100%;
}

.value-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(0,0,0,0.15);
}

.value-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
}

.value-icon i {
  font-size: 2rem;
  color: white;
}

.value-title {
  font-family: 'Playfair Display', serif;
  font-size: 1rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 15px;
}

.value-description {
  color: #718096;
  line-height: 1.7;
}

/* ========== OUR PROCESS SECTION ========== */
.our-process {
  padding: 20px 0;
  background: white;
}

.process-timeline {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.process-step {
  display: flex;
  gap: 30px;
  align-items: flex-start;
}

.step-number {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.3);
}

.step-content {
  flex: 1;
  padding-top: 10px;
}

.step-title {
  font-family: 'Playfair Display', serif;
  font-size: 1rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
  text-align: left;
}

.step-description {
  color: #718096;
  line-height: 1.7;
  font-size: 1.05rem;
  text-align: left;
}

/* ========== OUR TEAM SECTION ========== */
.our-team {
  padding: 20px 0;
  background: #f8fafb;
}

.team-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.4s;
}

.team-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(0,0,0,0.15);
}

.team-image-wrapper {
  position: relative;
  overflow: hidden;
}

.team-image {
  width: 100%;
  height: 350px;
  object-fit: cover;
  transition: transform 0.6s;
}

.team-card:hover .team-image {
  transform: scale(1.1);
}

.team-social {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.4s;
}

.team-card:hover .team-social {
  opacity: 1;
}

.social-link {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(118, 172, 175);
  transition: all 0.3s;
}

.social-link:hover {
  background: rgb(118, 172, 175);
  color: white;
  transform: scale(1.1);
}

.team-info {
  padding: 25px;
  text-align: center;
}

.team-name {
  font-family: 'Playfair Display', serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 5px;
}

.team-role {
  color: rgb(118, 172, 175);
  font-weight: 500;
}

/* ========== CTA SECTION ========== */
.cta-section {
  padding: 50px 0;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
}

.cta-content {
  text-align: center;
  color: white;
}

.cta-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  margin-bottom: 20px;
}

.cta-text {
  font-size: 1.2rem;
  margin-bottom: 35px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.cta-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-cta-primary,
.btn-cta-secondary {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 35px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.05rem;
  transition: all 0.3s;
  text-decoration: none;
}

.btn-cta-primary {
  background: white;
  color: rgb(118, 172, 175);
}

.btn-cta-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(255,255,255,0.3);
  color: rgb(98, 152, 155);
}

.btn-cta-secondary {
  background: rgba(255,255,255,0.2);
  color: white;
  border: 2px solid white;
}

.btn-cta-secondary:hover {
  background: white;
  color: rgb(118, 172, 175);
  transform: translateY(-3px);
}

/* ========== MOBILE RESPONSIVE ========== */
@media (max-width: 768px) {
  .page-hero {
    height: 300px;
    margin-top: 70px;
  }
  
  .our-story,
  .our-values,
  .our-process,
  .our-team,
  .cta-section {
    padding: 20px 0;
  }
  
  .story-image {
    height: 300px;
  }
  
  .story-badge {
    bottom: 15px;
    right: 15px;
    padding: 15px;
  }
  
  .process-step {
    flex-direction: column;
    gap: 20px;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .btn-cta-primary,
  .btn-cta-secondary {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
}
</style>